/**
 * Un reporte del catálogo mostrado DENTRO de una hoja del cierre.
 *
 * `cash_flow`, `cash_flow_summary` y `equity_changes` no son estados: son el
 * detalle por cuenta y la conciliación mensual de dos estados que sí lo son.
 * Como entradas del menú se leían como cuatro documentos distintos —dos de
 * ellos titulados igual que el estado oficial— y no hay cuatro documentos.
 *
 * No hace falta backend nuevo: se reusan las `columns` del `ReportSpec` que ya
 * existe y `api.report()`, con el período que la sección ya tiene. Sus rutas,
 * su API y sus exportaciones siguen intactas; cambia dónde se leen.
 *
 * La descarga NO va aquí: sale del menú único de la sección, que ofrece esta
 * vista cuando está abierta. Con botones propios, esta pantalla tendría dos
 * controles de exportación —el suyo y el de la hoja— que es lo que se acaba de
 * unificar en el resto de la aplicación.
 */

import { api } from "../../api/client";
import { useAsync } from "../../api/useAsync";
import { DataTable } from "../../components/DataTable";
import { ErrorState, Loading } from "../../components/States";
import { formatInteger } from "../../components/format";
import { useClosing } from "./closingContext";

/** Estos detalles son por cuenta o por mes: caben enteros y no se paginan. */
const LIMITE = 2000;

export function ReportPanel({
  reportKey,
  explanation,
}: {
  reportKey: string;
  /** Para qué sirve esta vista, dicho antes de la tabla y no después. */
  explanation: string;
}) {
  const { config, period } = useClosing();
  const spec = config.reports.find((report) => report.key === reportKey);
  const { data, error, loading } = useAsync(
    () => api.report(reportKey, period, LIMITE, 0),
    [reportKey, period.periodFrom, period.periodTo],
  );

  // Sin permiso para el reporte, el catálogo no lo trae. Se dice, en vez de
  // mostrar una tabla vacía que parece un período sin datos.
  if (!spec) {
    return (
      <p className="muted small">
        Este detalle no está disponible para su rol. El estado oficial de la
        pestaña anterior no depende de él.
      </p>
    );
  }

  return (
    <>
      <p className="muted small" style={{ maxWidth: "62ch" }}>
        {explanation}
      </p>

      {loading && <Loading what={spec.title.toLowerCase()} />}
      {error && <ErrorState error={error} />}
      {data && (
        <>
          <DataTable columns={spec.columns} rows={data.rows} />
          <p className="small muted" style={{ marginTop: 10 }}>
            {data.rows.length === 0
              ? "Sin filas para el período seleccionado."
              : `${formatInteger(data.rows.length)} de ${formatInteger(data.total)} filas.`}
            {data.total > data.rows.length &&
              " Descargue el XLSX para el detalle completo."}
          </p>
        </>
      )}
    </>
  );
}
